/**
 * Fieldset
 */

import React, { useState, useRef, useEffect } from 'react';
import { useFieldValidation } from '../hooks/useFieldValidation';
import { useFormValue } from '../context/Form.context';
import { FieldsetProps } from '../types';
import { ErrorSpan } from '../Labels';

const Fieldset: React.FC<FieldsetProps> = (props) => {
	const {
		label,
		description,
		children,
		id,
		required,
		validationMessage,
		isInvalid,
		showError,
		visibleFields,
		className,
	} = props;

	const [{ invalidFields }, dispatch] = useFormValue();
	const [valid, setValid] = useState(false);
	const [touched, setTouched] = useState(false);
	const fieldRef = useRef(null);
	const [validateField, showFieldError, hideFieldError] = useFieldValidation(
		fieldRef,
		props
	);

	useEffect(() => {
		const validatedField = validateField('fieldset');

		if (validatedField.message) {
			setValid(false);
			dispatch({ type: 'FIELD_INVALID', field: validatedField });
		} else {
			setValid(true);

			if (invalidFields.includes(validatedField.id)) {
				dispatch({ type: 'FIELD_VALID', field: validatedField });
			}
		}

		const partOfVisibleFields =
			visibleFields &&
			visibleFields.filter((fieldName) => fieldName.includes(id));

		if (touched && !valid) {
			showFieldError(validatedField);
		} else if (
			(showError &&
				!valid &&
				visibleFields &&
				partOfVisibleFields &&
				partOfVisibleFields.length > 0) ||
			(showError && !valid && !visibleFields)
		) {
			setTouched(true);
			showFieldError(validatedField);
		} else {
			hideFieldError();
		}
	}, [
		valid,
		dispatch,
		touched,
		showError,
		setValid,
		validateField,
		showFieldError,
		hideFieldError,
		invalidFields,
		visibleFields,
		id,
	]);

	return (
		<fieldset
			className={className}
			aria-invalid={!valid}
			id={id}
			title={description || undefined}
			ref={fieldRef}
			onBlur={() => setTouched(true)}
		>
			{description && <p className="text-sm text-[#595959]">{description}</p>}
			<legend className="block font-bold mb-1">
				{label}
				{required && <abbr className="no-underline" title="Obligatoriskt">*</abbr>}
			</legend>

			<div className="mb-6 flex flex-col gap-3 md:flex-row md:gap-6">{children}</div>

			<ErrorSpan fieldId={id} invalid={isInvalid} tabIndex={-1}>
				{validationMessage || ''}
			</ErrorSpan>
		</fieldset>
	);
};

export default Fieldset;
